<template>
  <StepContent>
    <div
      class="plan-selection"
      :class="foundationDeclined ? 'plan-selection-declined': 'plan-selection-foundation'"
    >
      <div class="plan-selection__header">
        <h1
          :class="textColorClass()"
          class="plan-selection__header-text"
        >
          {{ title }}
        </h1>
        <LottiePlayer
          class="plan-selection__header-animation"
          :animation-data="animationData"
          :height="120"
        />
      </div>
      <div class="plan-selection__body">
        <p class="plan-selection__body-text">
          The <span class="font-bold">Foundation</span> is our signature savings
          and credit building product that guarantees you up-front cash in as little as 12 months.
        </p>
      </div>
      <div class="sm:hidden">
        <div
          v-touch:end="handleEnd"
          v-touch:moving="handleMoving"
          class="plan-selection__cards sm:hidden"
          :class="isBloomFlow && 'plan-selection__cards--bloom'"
          :style="{transform: `translate(${translate}px)`}"
        >
          <PlanSelectionCard
            v-for="(item, index) in cards"
            :key="index"
            :class="`${featureCardClassName(item)} ${isBloomFlow && 'plan-selection__cards-card--bloom'}`"
            class="plan-selection__cards-card"
            :title="item.title"
            :subtitle="item.subtitle"
            :basic-card="item.basicCard"
            :features="item.features"
            :featured-card="item.featuredCard"
            :foundation-declined="foundationDeclined"
            :theme="theme"
            :add-banner="item.addBanner"
            is-mobile
            @clicked="handleClick"
          />
        </div>
        <div>
          <input
            type="radio"
            :checked="!featureRightCard"
            @input="handleRadioLeft"
          >
          <input
            type="radio"
            :checked="featureRightCard"
            @input="handleRadioRight"
          >
        </div>
      </div>

      <div class="xs-only:hidden">
        <div
          class="plan-selection__cards"
        >
          <PlanSelectionCard
            v-for="(item, index) in cards"
            :key="index"
            :class="`${featureCardClassName(item)} ${isBloomFlow && 'plan-selection__cards-card--bloom'}`"
            class="plan-selection__cards-card"
            :title="item.title"
            :subtitle="item.subtitle"
            :basic-card="item.basicCard"
            :features="item.features"
            :featured-card="item.featuredCard"
            :foundation-declined="foundationDeclined"
            :theme="theme"
            :add-banner="item.addBanner"
            @clicked="handleClick"
          />
        </div>
      </div>

      <div class="text-center">
        <span class="opacity-30">
          *Payment Amounts may vary slightly.
        </span>
        <AppButton
          class="plan-selection__continue-button"
          text="Continue"
          :animated="false"
          :theme="theme"
          @click="handleContinue"
        />
      </div>

      <AppFAQ
        class="plan-selection__FAQ"
        :questions="faqList"
        :theme="theme"
      />
    </div>
  </StepContent>
</template>

<script>
import StepContent from '@/components/common/step-layout/StepContent'
import navigation from '@/mixins/navigation'
import PlanSelectionCard from '@/components/common/cards/PlanSelectionCard'
import theme from '@/mixins/theme'
import LottiePlayer from '@/components/common/lottie/LottiePlayer'
import AppFAQ from '../../common/faq/AppFAQ'
import AppButton from '@/components/common/buttons/AppButton'
import submitLoans from '@/mixins/submit/submit-loans'

import DataLayer from '@/utilities/data-layer'

const MOVEMENT_THRESHOLD = 70
const RIGHT_CARD_DEFAULT_TRANSLATE = -295

export default {
  name: 'PlanSelection',

  components: {
    StepContent,
    PlanSelectionCard,
    AppFAQ,
    AppButton,
    LottiePlayer
  },

  mixins: [
    navigation,
    theme,
    submitLoans
  ],

  props: {
    title: {
      type: String,
      required: true
    },
    theme: {
      type: String,
      default: 'blue'
    },
    faq: {
      type: Array,
      default: () => []
    },
    foundationBloomFAQ: {
      type: Array,
      default: () => []
    },
    foundationDeclined: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      animationData: require('@/assets/lottie/foundation/seedling-flowers.json'),
      cards: [ {
        title: 'The Foundation Gold',
        subtitle: '$66 / bi-weekly *',
        features: [
          {
            text: 'Build Credit',
            answer: `Each payment is reported to TransUnion and Equifax. 
                      Making payments on time will help you improve your credit 
                      so you can qualify for the financing you want.`,
            checked: true,
            displayAnswer: false
          },
          {
            text: 'Monthly credit score updates',
            answer: `Get your updated credit score for free each month so you can keep track of your progress.`,
            checked: true,
            displayAnswer: false
          },
          {
            text: 'Cancel Anytime',
            answer: `You may cancel The Foundation at any time with no fees.`,
            checked: true,
            displayAnswer: false
          },
          {
            text: 'Save $750',
            answer: `Save $750 after 12 months. Over half of each full payment is saved for you to access at anytime! After 12 months of timely payments you will have saved $750.`,
            checked: true,
            displayAnswer: false
          },
          {
            text: 'Guaranteed loan offer',
            answer: `After 12 months and saving $750, you are guaranteed an up front cash loan for $1,500.`,
            checked: true,
            displayAnswer: false
          },
          {
            text: 'Bloom Budgeting App',
            answer: `Enjoy Full Access to the Bloom App. Your Comprehensive Money Management Tool! Simplify asset management, track spending habits, and gain insights into your financial trends.`,
            checked: true,
            displayAnswer: false,
            bloomOnly: true
          },
          {
            text: 'Rent Reporting',
            answer: `With Bloom rent reporting you add an additional trade line to your credit file giving your credit the extra boost that it needs. Easy to setup right in the Bloom app included with your Foundation payment.`,
            checked: true,
            displayAnswer: false,
            bloomOnly: true
          }
        ],
        basicCard: false,
        featuredCard: true,
        addBanner: true
      },
      {
        title: 'The Foundation Basic',
        subtitle: '$37 / bi-weekly *',
        features: [
          {
            text: 'Build Credit',
            answer: `Each payment is reported to TransUnion and Equifax. 
                      Making payments on time will help you improve your credit 
                      so you can qualify for the financing you want.`,
            checked: true,
            displayAnswer: false
          },
          {
            text: 'Monthly credit score updates',
            answer: `Get your updated credit score for free each month so you can keep track of your progress.`,
            checked: true,
            displayAnswer: false
          },
          {
            text: 'Cancel Anytime',
            answer: `You may cancel The Foundation at any time with no fees.`,
            checked: true,
            displayAnswer: false
          },
          {
            text: 'Save $750',
            answer: `<span class="font-bold">For Foundation Gold ONLY</span> Over half of each full payment will be saved for you to access at any time,
                       after 12 months of full payments you will have saved $750.`,
            checked: false,
            displayAnswer: false
          },
          {
            text: 'Guaranteed loan offer',
            answer: `<span class="font-bold">For Foundation Gold ONLY</span>. If you take the Foundation Gold and make 12 months of payments
                       and save $750 you will be guaranteed a $1500 up-front cash loan.`,
            checked: false,
            displayAnswer: false
          },
          {
            text: 'Bloom Budgeting App',
            answer: `Enjoy Full Access to the Bloom App. Your Comprehensive Money Management Tool! Simplify asset management, track spending habits, and gain insights into your financial trends.`,
            checked: false,
            displayAnswer: false,
            bloomOnly: true
          },
          {
            text: 'Rent Reporting',
            answer: `With Bloom rent reporting you add an additional trade line to your credit file giving your credit the extra boost that it needs. Easy to setup right in the Bloom app included with your Foundation payment.`,
            checked: false,
            displayAnswer: false,
            bloomOnly: true
          }
        ],
        basicCard: true,
        featuredCard: false
      }
      ],
      featureRightCard: false,
      xStartingPosition: undefined,
      translate: 0,
      translateStarted: false
    }
  },

  computed: {
    isFoundationGold () {
      return this.$store.state.foundation.isFoundationGold
    },
    updateLeadPayload () {
      let product = this.isFoundationGold ? 'Foundation Gold plus' : 'Foundation Basic plus'

      if (this.isBloomFlow) {
        return {
          Loan_Product__c: product,
          OPT_IN__c: this.isFoundationGold,
          PL_Declined_Returning_Cx__c: this.isReturningDeclinedCustomer
        }
      }

      return {
        Loan_Product__c: product,
        PL_Declined_Returning_Cx__c: this.isReturningDeclinedCustomer
      }
    },

    isBloomFlow () {
      return this.$store.state.foundation.isBloomFlow
    },

    faqList () {
      return this.isBloomFlow ? this.foundationBloomFAQ : this.faq
    },

    isReturningDeclinedCustomer () {
      return this.$store.state.foundation.returningDeclinedCustomer
    }
  },

  watch: {
    $_navigation_triggerContinue: async function (val) {
      if (val) this.handleContinue()
      this.$store.dispatch('setTriggerContinue', false)
    },

    isFoundationGold (value) {
      value ? this.setFoundationGold() : this.setFoundationBasic()
    }
  },

  created () {
    const bloomDL = this.isBloomFlow ? 'foundation-bloom-flow' : 'foundation-standard-flow'
    DataLayer.pushObject({ event: `spring_${this.eventPrefix}_plan_details_${bloomDL}` }, true)
    window.rudderanalytics.track(`spring_${this.eventPrefix}_plan_details_${bloomDL}`, {
      lead_source: this.leadSource,
      device: this.deviceType,
      returning_customer: this.returningCustomer
    })
    this.cards[0].subtitle = this.isBloomFlow ? '$71 / bi-weekly *' : '$66 / bi-weekly *'
    this.cards[1].subtitle = this.isBloomFlow ? '$37 / bi-weekly *' : '$37 / bi-weekly *'
  },

  mounted () {
    const foundationGold = this.$store.state.foundation.isFoundationGold
    if (!foundationGold) this.setFoundationBasic()
  },

  methods: {
    handleRadioLeft () {
      this.translate = 0
      this.$store.dispatch('setIsFoundationGold', true)
    },

    handleRadioRight () {
      this.translate = RIGHT_CARD_DEFAULT_TRANSLATE
      this.$store.dispatch('setIsFoundationGold', false)
    },

    handleSwipeLeft () {
      this.translate = RIGHT_CARD_DEFAULT_TRANSLATE
      this.$store.dispatch('setIsFoundationGold', false)
    },

    handleSwipeRight () {
      this.translate = 0
      this.$store.dispatch('setIsFoundationGold', true)
    },

    async handleContinue () {
      let planDetails = this.isFoundationGold ? 'gold-plus' : 'basic-plus'
      planDetails += this.isBloomFlow ? '-bloom' : '-standard'
      const eventDetails = {
        lead_source: this.leadSource,
        device: this.deviceType,
        returning_customer: this.returningCustomer
      }
      window.rudderanalytics.track(`spring_${this.eventPrefix}_plan_details_${planDetails}`, eventDetails)
      DataLayer.pushObject({ event: `spring_${this.eventPrefix}_plan_details_${planDetails}` }, true)
      await this.$_loans_updateLead(this.updateLeadPayload)
    },

    setFoundationGold () {
      this.featureRightCard = false
      this.cards[0].featuredCard = true
      this.cards[1].featuredCard = false
    },

    setFoundationBasic () {
      this.featureRightCard = true
      this.cards[0].featuredCard = false
      this.cards[1].featuredCard = true
    },

    handleClick (title) {
      const otherTitle = title === 'The Foundation Gold' ? 'The Foundation Basic' : 'The Foundation Gold'
      const clickedCard = this.cards.filter(card => { return card.title === title })[0]
      if (clickedCard.featuredCard !== true) {
        this.translate = null
        const currentFeaturedCard = this.cards.filter(card => { return card.title === otherTitle })[0]
        currentFeaturedCard.featuredCard = false
        clickedCard.featuredCard = true
        this.featureRightCard = clickedCard.title !== 'The Foundation Gold'
        this.$store.dispatch('setIsFoundationGold', !this.featureRightCard)
        if (this.featureRightCard) {
          this.translate = RIGHT_CARD_DEFAULT_TRANSLATE
        } else {
          this.translate = 0
        }
      }
    },

    featureCardClassName (card) {
      return card.featuredCard ? 'plan-selection__cards-card-featured' : 'plan-selection__cards-card-unfeatured'
    },

    handleMoving (event) {
      if (!(event.type === 'touchmove')) return
      const x = event.changedTouches[0].screenX
      if (this.translateStarted) {
        document.body.style.position = 'relative'
        document.body.style.overflow = 'hidden'
      }
      if (!this.xStartingPosition) {
        this.xStartingPosition = x
      } else {
        let delta = x - this.xStartingPosition
        if (this.featureRightCard && delta < 0) return
        if (!this.featureRightCard && delta > 0) return
        if (!this.featureRightCard && delta > -30) return
        if (this.featureRightCard && delta < 30) return
        this.translateStarted = true
        if (this.featureRightCard) {
          this.translate = RIGHT_CARD_DEFAULT_TRANSLATE + delta
        } else {
          this.translate = delta
        }
      }
    },

    handleEnd () {
      if (this.featureRightCard) {
        if ((RIGHT_CARD_DEFAULT_TRANSLATE - this.translate) <= -MOVEMENT_THRESHOLD) {
          this.handleSwipeRight()
        } else {
          this.translate = RIGHT_CARD_DEFAULT_TRANSLATE
        }
      } else {
        if (this.translate <= -MOVEMENT_THRESHOLD) {
          this.handleSwipeLeft()
        } else {
          this.translate = 0
        }
      }
      this.translateStarted = false

      document.body.style.position = 'static'
      document.body.style.overflow = 'visible'

      this.xStartingPosition = undefined
    }
  }
}
</script>

  <style scoped lang="scss">
  .plan-selection {
    @apply text-center;

    @screen lg {
      @apply mx-200;
    }

    &__header {
      @apply flex;

      @screen sm {
        @apply px-20 justify-center;
      }

      @screen md {
        @apply px-40;
      }

      &-text {
        @apply text-left;
      }

      &-animation {
        @apply pt-10;

        @screen sm {
          @apply hidden;
        }
      }
    }

    &__body {
      @apply relative;

      @screen sm {
        @apply flex;
      }

      &-text {
        @apply font-thin mt-auto mb-25 text-left;

        @screen sm {
          @apply mx-auto max-w-600 mt-20 text-center;
        }

        @screen lg {
          @apply max-w-550;
        }
      }

      &-animation {
        @screen xs-only {
          @apply hidden;
        }

        @apply absolute right-0 bottom-0;

        @screen md {
          right: 10%;
        }

        @screen lg {
          @apply right-0;
        }
      }
    }

    &__cards-container {
      @apply h-auto;
    }

    &__cards {
      @apply flex pb-15 md:overflow-x-hidden;

      @screen xs-only {
        @apply min-h-475 max-h-475 min-w-750;

        &--bloom {
          @apply min-h-580 max-h-580 min-w-750;
        }

        transition-duration: 200ms;
      }

      @screen sm {
        @apply w-full justify-center;
      }

      &-card {
        @apply min-w-300 max-w-300 min-h-450 max-h-450 sm:min-h-510 sm:max-h-530;

        &--bloom {
          @apply min-w-300 max-w-300 min-h-550 max-h-550 sm:min-h-550 sm:max-h-650;
        }

        @screen xs-only {
          transition: all 0.5s ease-in;
        }

        &-unfeatured {
          @screen xs-only {
            @apply min-h-360 max-h-360 mt-30 overflow-hidden opacity-50;

            transition: all 0.5s ease-out;
          }
        }
      }
    }

    &__FAQ {
      @apply float-left sm:float-none xs-only:p-0 mt-[120px];
    }

    &__continue-button {
      @apply my-20 mx-auto w-[280px];

      @screen xs-only {
        @apply hidden;
      }
    }

    input[type="radio"] {
      -webkit-appearance: none;
      -moz-appearance: none;

      @apply appearance-none m-5;

      &::before {
        @apply w-4 h-4 rounded-full top-0 left-[-1px] relative inline-block visible border-2;

        content: '';
      }
    }
  }

  .plan-selection-declined {
    input[type="radio"] {
      &::before {
        @apply border-blue-500;
      }

      &:checked::before {
        background: radial-gradient(#009dbd 0%, #009dbd 40%, transparent 50%, transparent);
      }
    }
  }

  .plan-selection-foundation {
    input[type="radio"] {
      &::before {
        @apply border-purple-500;
      }

      &:checked::before {
        background: radial-gradient(#5957c1 0%, #5957c1 40%, transparent 50%, transparent);
      }
    }
  }

  </style>
